import { Navigate, useNavigate, type NavigateFunction } from 'react-router-dom'

import { Alert } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/ErrorState'
import { LoadingState } from '@/components/ui/LoadingState'
import { PageHeader } from '@/components/ui/PageHeader'
import { useOperationVerificationStatus } from '@/hooks/useOperationVerificationStatus'
import {
  isVerificationExpiringSoon,
  OPERATION_VERIFICATION_EXPIRY_WARNING_NOTES,
  toFailureImpactNotes,
  toOperationVerificationSummaryViewModel,
} from '@/lib/venue/operationVerificationStatusViewModel'
import type { OperationVerificationView } from '@/lib/venue/operationVerificationTypes'
import { useVenueContext } from '@/routes/venueContext'

/**
 * 운영 인증 기한 임박 알림 — Figma `운영 인증 기한 임박 알림`(14039-40006, `#782`).
 *
 * 대시보드 "우선 처리 항목"(`actionType=OPERATION_VERIFICATION`)의 유일한 진입점이다(설계 §2 자율
 * 결정 1). 조회 전용 — "인증 진행하기"는 기존 신청 폼(`operation-verification`)으로 `navigate`한다.
 * 🚨 신규 API 없음 — `#778`이 이미 소비 중인 EP-A(`useOperationVerificationStatus`)를 그대로 쓴다.
 */
export function VenueOperationVerificationExpiryNoticePage() {
  const { venueId } = useVenueContext()
  const navigate = useNavigate()
  const statusQuery = useOperationVerificationStatus(venueId)

  return (
    <>
      <PageHeader title="운영 인증 기한 알림" />

      {statusQuery.isLoading ? <LoadingState rows={4} label="인증 현황을 불러오는 중" /> : null}

      {statusQuery.isError ? (
        <ErrorState
          error={statusQuery.error}
          onRetry={() => {
            void statusQuery.refetch()
          }}
        />
      ) : null}

      {statusQuery.data ? <NoticeContent venueId={venueId} view={statusQuery.data} navigate={navigate} /> : null}
    </>
  )
}

function NoticeContent({
  venueId,
  view,
  navigate,
}: {
  venueId: string
  view: OperationVerificationView
  navigate: NavigateFunction
}) {
  if (isVerificationExpiringSoon(view)) {
    return <ExpiringSoonNotice venueId={venueId} view={view} navigate={navigate} />
  }
  if (view.badge === 'EXPIRED') {
    return <ExpiredNotice venueId={venueId} view={view} navigate={navigate} />
  }

  // 🚨 설계 §2 자율 결정 5 — 우선 처리 항목이 정상 경로로만 이 화면을 링크하는 한 발생하지 않지만,
  // 북마크·뒤로가기 등으로 상태가 바뀐 뒤 도달할 수 있다. `#778`의 status 페이지가 5상태를 이미 전부
  // 정확히 다루므로 새로 분기를 만들지 않고 위임한다(Surgical — 로직 중복 금지).
  return <Navigate to={`/venues/${venueId}/operation-verification/status`} replace />
}

function ProceedButton({ venueId, navigate }: { venueId: string; navigate: NavigateFunction }) {
  return (
    <Button
      onClick={() => {
        navigate(`/venues/${venueId}/operation-verification`)
      }}
    >
      인증 진행하기
    </Button>
  )
}

/** "임박" 변형 — 인증은 아직 유효하다(`badge==='VERIFIED'`), 기한이 다가오고 있음을 알린다. */
function ExpiringSoonNotice({
  venueId,
  view,
  navigate,
}: {
  venueId: string
  view: OperationVerificationView
  navigate: NavigateFunction
}) {
  const vm = toOperationVerificationSummaryViewModel(view)

  return (
    <div className="space-y-5">
      <Card>
        <CardBody className="space-y-2 py-10 text-center">
          <Badge tone="warning">{vm.badgeLabel}</Badge>
          <p className="text-lg font-semibold text-ink-700">운영 인증 기한이 {view.remainingDays}일 남았어요</p>
          <p className="text-sm text-slateish-600">{vm.verifiedUntilText}까지 인증을 완료해 주세요</p>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="기한이 지나면 어떻게 되나요" />
        <CardBody>
          <ul className="list-disc space-y-1.5 pl-5 text-sm text-ink-700">
            {OPERATION_VERIFICATION_EXPIRY_WARNING_NOTES.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </CardBody>
      </Card>

      <div className="flex justify-end">
        <ProceedButton venueId={venueId} navigate={navigate} />
      </div>
    </div>
  )
}

/** "기한 초과" 변형 — 인증이 이미 만료됐다(`badge==='EXPIRED'`), 재인증 경로를 안내한다(요구사항 3). */
function ExpiredNotice({
  venueId,
  view,
  navigate,
}: {
  venueId: string
  view: OperationVerificationView
  navigate: NavigateFunction
}) {
  const vm = toOperationVerificationSummaryViewModel(view)
  const impactNotes = toFailureImpactNotes(view)

  return (
    <div className="space-y-5">
      <Card>
        <CardBody className="space-y-2 py-10 text-center">
          <Badge tone={vm.tone}>{vm.badgeLabel}</Badge>
          <p className="text-lg font-semibold text-ink-700">운영 인증 기한이 지났어요</p>
          <p className="text-sm text-slateish-600">인증 기한 {vm.verifiedUntilText}</p>
        </CardBody>
      </Card>

      <Alert tone="danger" title="지금 어떤 영향이 있나요">
        <ul className="list-disc space-y-1.5 pl-5">
          {impactNotes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      </Alert>

      <div className="flex justify-end">
        <ProceedButton venueId={venueId} navigate={navigate} />
      </div>
    </div>
  )
}
